import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Heart } from "lucide-react";
import { API } from "@/lib/api";
import { logError } from "@/lib/log";

const SESSION_KEY = "aloha_chat_session";

function newSessionId() {
  return "chat_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function newMessageId() {
  return "msg_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "msg_welcome", role: "assistant", text: "Aloha! 🌺 I'm the Aloha Assistant. Ask me about adopting, fostering, our dogs, or how to help." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newSessionId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    setSessionId(id);
  }, []);

  // Listen for "aloha-chat" custom events (e.g., from dog cards)
  useEffect(() => {
    const handler = (e) => {
      const message = e.detail?.message;
      if (!message) return;
      setOpen(true);
      setInput(message);
    };
    window.addEventListener("aloha-chat", handler);
    return () => window.removeEventListener("aloha-chat", handler);
  }, []);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setInput("");
    const userId = newMessageId();
    const streamId = newMessageId();
    setMessages((m) => [
      ...m,
      { id: userId, role: "user", text },
      { id: streamId, role: "assistant", text: "", streaming: true },
    ]);
    setSending(true);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { id: streamId, role: "assistant", text: acc, streaming: true };
          return copy;
        });
      }
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { id: streamId, role: "assistant", text: acc || "…" };
        return copy;
      });
    } catch (err) {
      logError("[ChatWidget] streaming failed:", err);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { id: streamId, role: "assistant", text: "Sorry, my ʻohana lost signal. Please try again or email us." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 rounded-full shadow-lg flex items-center gap-2 px-5 py-4 text-white transition-transform hover:-translate-y-1"
          style={{ backgroundColor: "#E27E4E", boxShadow: "0 12px 32px rgba(194,103,83,0.4)" }}
          data-testid="chat-open-button"
        >
          <MessageCircle size={20} />
          <span className="font-medium">Aloha!</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[92vw] max-w-sm rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          style={{ backgroundColor: "#FDF9F1", height: "min(72vh, 620px)", border: "1px solid #EADCC5" }}
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between p-4" style={{ backgroundColor: "#0E5C6E", color: "white" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "#E27E4E" }}>
                <Heart size={16} fill="white" />
              </div>
              <div>
                <div className="font-serif text-lg leading-tight">Aloha Assistant</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Powered by ʻohana</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="chat-close-button">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "text-white" : ""
                  }`}
                  style={{
                    backgroundColor: m.role === "user" ? "#0E5C6E" : "#F7ECD9",
                    color: m.role === "user" ? "white" : "#2D2B2A",
                    borderBottomRightRadius: m.role === "user" ? 4 : undefined,
                    borderBottomLeftRadius: m.role === "user" ? undefined : 4,
                  }}
                >
                  {m.text || (m.streaming ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="p-3 flex gap-2 border-t" style={{ borderColor: "#EADCC5" }} data-testid="chat-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about adopting, fostering, or a specific dog…"
              className="flex-1 px-4 py-3 rounded-full text-sm focus:outline-none"
              style={{ backgroundColor: "#F7ECD9", color: "#2D2B2A" }}
              disabled={sending}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="rounded-full w-11 h-11 flex items-center justify-center text-white disabled:opacity-50"
              style={{ backgroundColor: "#E27E4E" }}
              data-testid="chat-send-button"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
